/* eslint-disable */
import React, { useState, useEffect, useRef } from 'react';
import jsQR from 'jsqr';

/**
 * PhoneScannerScreen — QR code scanner with camera fallback to manual input.
 * Extracted from App.js to share between PhoneView and Deployment mobile views.
 *
 * Props:
 *   onScan(code: string) — called when a QR code is decoded or manual entry submitted
 *   compact — if true, renders as a smaller inline scanner (160px) with 1500ms debounce
 */
export default function PhoneScannerScreen({onScan,compact}){
  const videoRef=useRef();const canvasRef=useRef();
  const [camErr,setCamErr]=useState(false);
  const [manual,setManual]=useState('');
  const cbRef=useRef(onScan);
  useEffect(()=>{cbRef.current=onScan;},[onScan]);
  useEffect(()=>{
    let stream,rafId,alive=true;
    navigator.mediaDevices?.getUserMedia({video:{facingMode:{ideal:'environment'}}})
      .then(s=>{if(!alive){s.getTracks().forEach(t=>t.stop());return;}stream=s;if(videoRef.current){videoRef.current.srcObject=s;videoRef.current.play();}})
      .catch(()=>setCamErr(true));
    const tick=()=>{
      if(!alive)return;
      const v=videoRef.current,c=canvasRef.current;
      if(v?.readyState===4&&c){
        c.height=v.videoHeight;c.width=v.videoWidth;
        const ctx=c.getContext('2d');ctx.drawImage(v,0,0,c.width,c.height);
        const id=ctx.getImageData(0,0,c.width,c.height);
        const code=jsQR(id.data,id.width,id.height,{inversionAttempts:'dontInvert'});
        if(code?.data){if(!compact){alive=false;stream?.getTracks().forEach(t=>t.stop());cbRef.current(code.data);return;}cbRef.current(code.data);alive=false;setTimeout(()=>{alive=true;rafId=requestAnimationFrame(tick);},1500);return;}
      }
      rafId=requestAnimationFrame(tick);
    };
    rafId=requestAnimationFrame(tick);
    return()=>{alive=false;cancelAnimationFrame(rafId);stream?.getTracks().forEach(t=>t.stop());};
  },[]);
  if(camErr)return(
    <div style={{flex:1,background:'#f5f6fa',display:'flex',flexDirection:'column',alignItems:'center',justifyContent:'center',padding:'32px 24px',gap:20}}>
      <div style={{width:80,height:80,borderRadius:24,background:'#fff0eb',display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
        <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="#FF530D" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"><path d="M23 19a2 2 0 0 1-2 2H3a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h4l2-3h6l2 3h4a2 2 0 0 1 2 2z"/><circle cx="12" cy="13" r="4"/></svg>
      </div>
      <div style={{textAlign:'center'}}>
        <div style={{fontSize:18,fontWeight:700,color:'#1a1a2e',marginBottom:6}}>Camera Access Required</div>
        <p style={{fontSize:13,color:'#888',margin:0,lineHeight:1.6}}>Camera access denied.<br/>Enter the serial number manually:</p>
      </div>
      <input value={manual} onChange={e=>setManual(e.target.value)} onKeyDown={e=>e.key==='Enter'&&manual.trim()&&cbRef.current(manual.trim())} placeholder="e.g. 21001080" style={{width:'100%',padding:'13px 16px',borderRadius:14,border:'1px solid #ddd',background:'#fff',color:'#1a1a2e',fontSize:15,outline:'none',boxSizing:'border-box',boxShadow:'0 1px 4px rgba(0,0,0,0.06)'}}/>
      <button onClick={()=>manual.trim()&&cbRef.current(manual.trim())} disabled={!manual.trim()} style={{width:'100%',padding:'14px',borderRadius:14,border:'none',background:manual.trim()?'#FF530D':'#ddd',color:'#fff',fontWeight:700,fontSize:15,cursor:manual.trim()?'pointer':'default'}}>Look Up Device</button>
    </div>
  );
  const boxW=compact?160:Math.min(window.innerWidth-48,300);
  return(
    <div style={{flex:1,background:compact?'var(--surface-inset)':'#f5f6fa',display:'flex',flexDirection:compact?'row':'column',alignItems:'center',padding:compact?'12px 16px':'28px 24px 16px',gap:compact?16:0,overflow:'hidden'}}>
      {!compact&&<div style={{textAlign:'center',marginBottom:24,width:'100%'}}>
        <div style={{fontSize:22,fontWeight:800,color:'#1a1a2e',marginBottom:6}}>Scan QR Code</div>
        <div style={{fontSize:13,color:'#aaa',lineHeight:1.5}}>Place the device QR code inside the frame</div>
      </div>}
      {/* Camera box */}
      <div style={{width:boxW,height:boxW,borderRadius:compact?12:24,overflow:'hidden',position:'relative',boxShadow:'0 4px 16px rgba(0,0,0,0.14)',flexShrink:0,background:'#111'}}>
        <video ref={videoRef} style={{width:'100%',height:'100%',objectFit:'cover'}} playsInline muted/>
        <canvas ref={canvasRef} style={{display:'none'}}/>
        {/* Corner brackets */}
        <div style={{position:'absolute',top:10,left:10,width:22,height:22,borderTop:'3px solid #FF530D',borderLeft:'3px solid #FF530D',borderRadius:'4px 0 0 0',pointerEvents:'none'}}/>
        <div style={{position:'absolute',top:10,right:10,width:22,height:22,borderTop:'3px solid #FF530D',borderRight:'3px solid #FF530D',borderRadius:'0 4px 0 0',pointerEvents:'none'}}/>
        <div style={{position:'absolute',bottom:10,left:10,width:22,height:22,borderBottom:'3px solid #FF530D',borderLeft:'3px solid #FF530D',borderRadius:'0 0 0 4px',pointerEvents:'none'}}/>
        <div style={{position:'absolute',bottom:10,right:10,width:22,height:22,borderBottom:'3px solid #FF530D',borderRight:'3px solid #FF530D',borderRadius:'0 0 4px 0',pointerEvents:'none'}}/>
      </div>
      {compact
        ?<div style={{display:'flex',flexDirection:'column',gap:4}}>
          <div style={{fontSize:13,fontWeight:700,color:'var(--text-primary)'}}>Scan QR Code</div>
          <div style={{fontSize:12,color:'var(--text-muted)',lineHeight:1.4}}>Place the device QR code<br/>inside the frame</div>
          <div style={{fontSize:11,color:'var(--text-muted)',marginTop:4}}>Scanning…</div>
        </div>
        :<div style={{fontSize:13,color:'#bbb',letterSpacing:0.3,marginTop:20}}>Scanning Code…</div>}
    </div>
  );
}
